/**
 * Authentication Hooks
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authService } from '../services/auth';
import type { LoginRequest, TokenRefreshRequest } from '../types';

export const authKeys = {
  all: ['auth'] as const,
  status: () => [...authKeys.all, 'status'] as const,
};

/**
 * Hook to check current authentication status
 */
export function useAuthStatus() {
  return useQuery({
    queryKey: authKeys.status(),
    queryFn: () => authService.getStatus(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
}

/**
 * Hook to perform login
 */
export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: LoginRequest) => authService.login(request),
    onSuccess: (data) => {
      // Store tokens
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      // Invalidate auth status
      queryClient.invalidateQueries({ queryKey: authKeys.status() });
    },
  });
}

/**
 * Hook to refresh access token
 */
export function useRefreshToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: TokenRefreshRequest) => authService.refreshToken(request),
    onSuccess: (data) => {
      localStorage.setItem('accessToken', data.accessToken);
      queryClient.invalidateQueries({ queryKey: authKeys.status() });
    },
  });
}

/**
 * Hook to perform logout
 */
export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authService.logout(),
    onSettled: () => {
      // Clear tokens regardless of success/failure
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      // Clear all cached data
      queryClient.clear();
    },
  });
}
